import type { UserField, UserList, SearchUsersResponse } from 'types/UserZendesk';
import type { IdentitesUser, ZAFClient } from './interface';
import type { HistoryResponse } from 'types/UserZendesk';
export const zafClient = (window as Window).zafClient;
export const urlZaf = zafClient._origin;

declare global {
  interface Window {
    zafClient: ZAFClient;
  }
}
export function popupError(string: string, sticky: boolean) {
  zafClient.invoke("notify", string, "error", { sticky });
}

export function popupSuccess(string: string, sticky: boolean) {
  zafClient.invoke("notify", string, "success", { sticky });
}


export async function getCurrentTicketNumber(zafClient: ZAFClient) {
  try {
    const ticket = await zafClient.get('ticket');
    return (ticket as unknown as { ticket: { id: number } }).ticket.id;
  } catch (error) {
    console.error('Error getting ticket number:', error);
    return null;
  }
}

export async function getCustomObjects(zafClient: ZAFClient, ticket: number) {
  try {
    
    const response = await zafClient.request({
      url: `/api/v2/tickets/${ticket}?remove_duplicate_fields=true&include=brands%2Cpermissions%2Cusers%2Cgroups%2Corganizations%2Csharing_agreements%2Cincident_counts%2Ctde_workspace%2Cslas%2Clookup_relationship_fields%2Ccustom_statuses%2Cgroup_slas`,
      type: "GET",
    });
    const ticketData = response as { lookup_relationship_fields: { [key: string]: any } };
    
    const customObjects = ticketData.lookup_relationship_fields?.["zen:custom_object:contas_do_ticket"];
    
    return customObjects || [];
  } catch (error) {
    if (error instanceof Error) {
      popupError(error.message, false);
    } else {
      popupError("Verifique se o campo personalizado 'Contas do Ticket' está configurado corretamente.", true);
    }
    return [];
  }
}

export async function searchCustomObjectByName(
  zafClient: ZAFClient,
  name: string
) {
  try {
    const response = await zafClient.request({
      url: `/api/v2/custom_objects/contas_do_ticket/records/search?query=name:${name}`,
      type: "GET",
    });
    return response as { custom_object_records: { id: string; name: string }[] };
  } catch (error) {
    console.error("Error searching custom object:", error);
    return null;
  }
}
export async function getFieldNames(zafClient: ZAFClient) {
     const ticketFields = await zafClient.request({
      url: `/api/v2/ticket_fields`,
      type: "GET",
    });

    return (ticketFields as { ticket_fields: UserField[] }).ticket_fields.find(f => f.title.includes("Contas"));
}

export async function createCustomObject(
  zafClient: ZAFClient,
  ticketNumber: number,
  name: string
) {
  try {
    const response = await zafClient.request({
      url: "/api/v2/custom_objects/contas_do_ticket/records.json",
      type: "POST",
      data: {
        custom_object_record: {
          name: name,
          custom_object_fields: {
            ticket_id: ticketNumber.toString()
          }
        }
      }
    });

    const customObjectResponse = response as { custom_object_record: { id: string } };
    const customObjectId = customObjectResponse.custom_object_record.id as string;
    const field = await getFieldNames(zafClient);
    const fieldContaString = field?.id?.toString();

    await zafClient.request({
      url: `/api/v2/tickets/${ticketNumber}.json`,
      type: "PUT",
      dataType: "json",
      contentType: "application/json",
      data: JSON.stringify({
        ticket: {
          custom_fields: {
            "zen:custom_object:contas_do_ticket": customObjectId,
            ...(fieldContaString && { [fieldContaString]: customObjectId })
          }
        }
      })
    });
   
    
    return customObjectId;
  } catch (error) {
    console.error("Error creating custom object:", error);
    throw error;
  }
}

export async function checkCpfExists(zafClient: ZAFClient, cpf: string) {
  try {
    const rawCpf = cpf.replace(/\D/g, '');
    const response = await zafClient.request({
      url: `/api/v2/users/search.json?query=cpf_3:${cpf}`,
      type: "GET",
    });

    const users = response as { users: UserList[] };

    if (users.users.length === 0) {
      const externalIdResponse = await zafClient.request({
        url: `/api/v2/users/search.json?external_id=${rawCpf}`,
        type: "GET",
      });
      const externalIdUsers = externalIdResponse as { users: UserList[] };
      
      return {
        exists: externalIdUsers.users.length > 0,
        user: externalIdUsers.users[0] || null
      };
    }

    return {
      exists: users.users.length > 0,
      user: users.users[0] || null
    };
  } catch (error) {
    console.error("Error checking CPF:", error);
    return {
      exists: false,
      user: null
    };
  }
}

export async function checkCnpjExists(zafClient: ZAFClient, cnpj: string) {
  try {
    const cleanedCnpj = cnpj.replace(/\D/g, '');
    const response = await zafClient.request({
      url: `/api/v2/users/search.json?query=cnpj_regex:${cleanedCnpj}`,
      type: "GET",
    });

    const users = response as { users: UserList[] };

    if (users.users.length === 0) {
      const externalIdResponse = await zafClient.request({
        url: `/api/v2/users/search.json?external_id=${cleanedCnpj.replace(/\D/g, '')}`,
        type: "GET",
      });
      const externalIdUsers = externalIdResponse as { users: UserList[] };
      
      return {
        exists: externalIdUsers.users.length > 0,
        user: externalIdUsers.users[0] || null
      };
    }

    return {
      exists: users.users.length > 0,
      user: users.users[0] || null
    };
  } catch (error) {
    console.error("Error checking CNPJ:", error);
    return {
      exists: false,
      user: null
    };
  }
}

export async function checkExternalIdExists(zafClient: ZAFClient, externalId: string) {
  try {
    const cleanedExternalId = externalId.replace(/\D/g, '');
    const response = await zafClient.request({
      url: `/api/v2/users/search.json?external_id=${cleanedExternalId}`,
      type: "GET",
    });

    const users = response as { users: UserList[] };

    return {
      exists: users.users.length > 0,
      user: users.users[0] || null
    };
  } catch (error) {
    console.error("Error checking external_id:", error);
    return {
      exists: false,
      user: null
    };
  }
}



export async function searchUsers(zafClient: ZAFClient, query: string) {
  try {
    const response = await zafClient.request({
      url: `/api/v2/users/search.json?query=${encodeURIComponent(query)}`,
      type: "GET",
    });

    const { users } = response as SearchUsersResponse;
    return users;
  } catch (error) {
    console.error("Error searching users:", error);
    return [];
  }
}

export async function createZendeskUser(
  zafClient: ZAFClient,
  userData: {
    name: string;
    email: string;
    phone: string;
    external_id?: string;
    user_fields: Record<string, any>;
  }
) {
  try {
    const response = await zafClient.request({
      url: "/api/v2/users.json",
      type: "POST",
      data: {
        user: {
          name: userData.name,
          email: userData.email,
          phone: userData.phone,
          external_id: userData.external_id,
          user_fields: userData.user_fields,
          tags: ["app-contas"]
        }
      }
    });
    const createdUser = response as UserList;
    return createdUser.user;
  } catch (error) {
    console.error("Error creating user:", error);
    throw error;
  }
}

export async function getHistoryUser(zafClient: ZAFClient, userId: number) {
  try {
    const response = await zafClient.request({
      url: `/api/v2/audit_logs?filter[source_type]=user&filter[source_id]=${userId}`,
      type: "GET",
    });
    const historyUser = response as HistoryResponse;
    return historyUser.audit_logs || [];
  } catch (error) {
    console.error("Error searching users:", error);
    return [];
  }
}


export async function loadUserFields(): Promise<UserField[]> {
  const response = await zafClient.request({
    url: '/api/v2/user_fields.json',
    type: "GET",
  });
  const userFields = response as { user_fields: UserField[] };

  return userFields.user_fields;
}

export const getCurrentUser = async (client: any) => {
  const currentUser = await client.get('currentUser');
  return currentUser.currentUser;
};


export async function getMyUser(zafClient: ZAFClient) {
  try {
    const response = await zafClient.request({
      url: `/api/v2/users/me`,
      type: "GET",
    });
    return response;
  } catch (error) {
    console.error("Error getting my user:", error);
    return null;
  }
}



export async function updatePrimaryEmail(userId: number, newEmail: string) {
  const identitiesRes = await zafClient.request({
    url: `/api/v2/users/${userId}/identities.json`,
    type: 'GET',
  }) as { identities: IdentitesUser[] };

  const identities = identitiesRes.identities;

  const existingIdentity = identities.find(
    i => i.type === 'email' && i.value === newEmail
  );

  let identityId: number;

  if (existingIdentity) {
    identityId = existingIdentity.id;
  } else {
    const createRes = await zafClient.request({
      url: `/api/v2/users/${userId}/identities.json`,
      type: 'POST',
      data: {
        identity: {
          type: 'email',
          value: newEmail,
          verified: false,
        }
      }
    }) as { identity: { id: number } };

    identityId = createRes.identity.id;
  }

  await zafClient.request({
    url: `/api/v2/users/${userId}/identities/${identityId}/make_primary.json`,
    type: 'PUT',
  });
}